/**
 * DateRangePickerComponent
 *
 * Date range selection component with presets
 * Renders two date inputs with optional preset shortcuts
 */

import React, { useCallback, useMemo, memo } from 'react';
import type { FormControlProps } from '../../types';

interface DatePreset {
  label: string;
  rangeId: string;
  daysFromNow: number;
}

interface DateRangeValue {
  startDate: string;
  endDate: string;
}

interface DateRangePickerComponentProps extends FormControlProps {
  defaultStartDate?: string;
  defaultEndDate?: string;
  dateFormat?: string;
  presets?: DatePreset[];
  minDate?: string;
  maxDate?: string;
  onChange?: (value: DateRangeValue) => void;
}

function getDateNDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
}

export const DateRangePickerComponent: React.FC<DateRangePickerComponentProps> = memo(({
  value = { startDate: '', endDate: '' },
  defaultStartDate = '',
  defaultEndDate = '',
  dateFormat = 'YYYY-MM-DD',
  presets = [],
  minDate,
  maxDate,
  onChange,
  disabled = false,
  required = false,
  label,
  error,
  helpText,
}) => {
  const rangeValue = useMemo(() => {
    if (typeof value === 'object' && value !== null) {
      return value as DateRangeValue;
    }
    return { startDate: defaultStartDate, endDate: defaultEndDate };
  }, [value, defaultStartDate, defaultEndDate]);

  const inputId = useMemo(
    () => `date-range-${label?.replace(/\s+/g, '-').toLowerCase()}`,
    [label],
  );

  const handleStartDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.({
        startDate: e.target.value,
        endDate: rangeValue.endDate,
      });
    },
    [rangeValue.endDate, onChange],
  );

  const handleEndDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.({
        startDate: rangeValue.startDate,
        endDate: e.target.value,
      });
    },
    [rangeValue.startDate, onChange],
  );

  const handlePresetClick = useCallback(
    (preset: DatePreset) => {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = getDateNDaysAgo(preset.daysFromNow);
      onChange?.({ startDate, endDate });
    },
    [onChange],
  );

  return (
    <div className="af-form-field__group date-range-picker-group">
      {label && (
        <label className="af-form-field__label">
          {label}
          {required && <span className="af-form-field__required">*</span>}
        </label>
      )}

      <div className="date-range-picker__container">
        <div className="date-range-picker__inputs">
          <div className="date-range-picker__input-group">
            <label htmlFor={`${inputId}-start`} className="date-range-picker__input-label">
              Start Date
            </label>
            <input
              id={`${inputId}-start`}
              type="date"
              value={rangeValue.startDate}
              onChange={handleStartDateChange}
              min={minDate}
              max={maxDate}
              disabled={disabled}
              className="date-range-picker__input"
              aria-label="Start date"
              aria-invalid={!!error}
            />
          </div>

          <div className="date-range-picker__input-group">
            <label htmlFor={`${inputId}-end`} className="date-range-picker__input-label">
              End Date
            </label>
            <input
              id={`${inputId}-end`}
              type="date"
              value={rangeValue.endDate}
              onChange={handleEndDateChange}
              min={minDate}
              max={maxDate}
              disabled={disabled}
              className="date-range-picker__input"
              aria-label="End date"
              aria-invalid={!!error}
            />
          </div>
        </div>

        {presets.length > 0 && (
          <div className="date-range-picker__presets">
            {presets.map((preset) => (
              <button
                key={preset.rangeId}
                type="button"
                onClick={() => handlePresetClick(preset)}
                disabled={disabled}
                className="date-range-picker__preset-button"
                aria-label={`Select preset: ${preset.label}`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div id={`${inputId}-error`} className="af-form-field__error">
          {error}
        </div>
      )}

      {helpText && (
        <div id={`${inputId}-help`} className="af-form-field__help">
          {helpText}
        </div>
      )}
    </div>
  );
});
